import React from 'react';
import PropTypes from 'prop-types';
import styles from './choiceInteraction.scss';
import choiceSequenceHandler from './../../../../qti/utils/ChoiceSequenceHandler';
import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import Prompt from '../Prompt';
import LoadTracker from '../../../../services/LoadTracker';
import stringUtils from '../../../../qti/utils/StringUtils';
import { showMedia } from '../../media/MediaDisplay';
import interactionVisibilityService from '../../../../services/questionView/InteractionVisibilityService';
import playSequenceService from '../../../../services/PlaySequenceService';
import constants from '../../../../qti/Constants';
import utilService from '../../../../services/UtilService';
import { t } from '../../../../services/i18nService';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import customFieldsService from '../../../../services/CustomFieldsService';
import customFieldsConstants from '../../../../constants/CustomFieldsConstants';
import QuestionRubricBlock from '../../QuestionRubricBlock';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import domService from '../../../../services/DomService';
import themeServices from '../../../../themes/ThemeServices';
import UIConstants from '../../../../constants/UIConstants';

const audioElements = {};

function getAlternatives(model, choiceSequence) {
    const alternatives = Array.from(model.alternatives);
    if (choiceSequence !== undefined) {
        choiceSequenceHandler.sortAlternativesAccordingToChoiceSequence(alternatives, choiceSequence);
    }
    return alternatives;
}

function getMediaModels(interactionModel, choiceSequence) {
    return getAlternatives(interactionModel, choiceSequence)
        .filter((alternative) => {
            return Array.isArray(alternative.mediaModels) && alternative.mediaModels.length > 0;
        })
        .map((alternative) => {
            return alternative.mediaModels[0];
        });
}

function renderSource(data) {
    const { url, mimeType = null } = data;
    return <source key={url} src={url} type={mimeType} />;
}

export function renderAudioElements(interactionModel, choiceSequence) {
    return (
        <div className="audioContainer">
            {
                getMediaModels(interactionModel, choiceSequence)
                    .filter(mediaModel => mediaModel.type === constants.mediaType.AUDIO)
                    .map((mediaModel) => {
                        const { logicalName, sources = [], url } = mediaModel;
                        if (url) {
                            sources.push({ url });
                        }
                        const id = `media-${logicalName}`;
                        audioElements[id] = React.createRef();
                        return (
                            <audio // eslint-disable-line jsx-a11y/media-has-caption
                                id={id}
                                key={logicalName}
                                preload={utilService.isIOS() ? 'none' : 'auto'}
                                ref={audioElements[id]}
                            >
                                {sources.map(renderSource)}
                            </audio>
                        );
                    })
            }
        </div>
    );
}

class ChoiceInteraction extends React.PureComponent {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        choiceSequence: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isPreRender: PropTypes.bool,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        correctResponses: PropTypes.array, // array with simpleChoice identifiers
        questionModel: PropTypes.object.isRequired,
        services: PropTypes.object,
        viewMedia: PropTypes.string.isRequired,
    };

    constructor(props) {
        super(props);
        this.sortedAlternatives = getAlternatives(props.model, props.choiceSequence);
        this.instanceId = stringUtils.getUID();
        this.iconTrackers = {};
        this.masterTracker = new LoadTracker(this.props.handleInteractionReady, `Interaction ${this.props.model.identifier}`);
        this.onInputReady = this.masterTracker.addItemToTrack(`Interaction ${props.model.identifier} input`);
        if (props.correctResponses !== undefined) {
            this.sortedAlternatives.forEach(({ identifier }) => {
                this.iconTrackers[identifier] = this.masterTracker.addItemToTrack(`outcomeIcon ${identifier}`);
            });
        }
        this.mediaTracker = this.masterTracker.addItemToTrack(`Interaction ${props.model.identifier} media`);
        this.masterTracker.setAllItemsAdded();

        this.state = {
            isMediaSequencePlaying: false,
        };
    }

    componentDidMount() {
        this.onInputReady(true);
        const { isPreRender, model, questionModel, services, viewMedia } = this.props;

        const promises = this
            .sortedAlternatives
            .reduce((accumulator, alternative) => {
                if (Array.isArray(alternative.mediaModels)) {
                    return accumulator.concat(
                        alternative.mediaModels
                            .map((mediaModel) => {
                                const showMediaParams = {
                                    model: mediaModel,
                                    services,
                                    displayContext: 'choiceOption',
                                    isPreRender,
                                    isInteraction: true,
                                };

                                const ref = audioElements[`media-${mediaModel.logicalName}`];
                                if (ref !== undefined && ref.current !== undefined) {
                                    return showMedia({ ...showMediaParams, mediaElement: ref.current });
                                }

                                return showMedia(showMediaParams);
                            })
                    );
                }
                return accumulator;
            }, []);

        Promise
            .all(promises)
            .then(() => this.mediaTracker(true))
            .then(() => {
                if (interactionVisibilityService.shouldTriggerAutoPlay(model.identifier, questionModel, viewMedia)) {
                    this.autoPlayTriggerHandle = setTimeout(() => this.startPlayingMediaSequence(), 1000);
                }
            })
            .catch((error) => {
                this.mediaTracker(false);
                window.console.log(error);
            });
    }

    componentWillUnmount() {
        this.stopPlayingMediaSequence();
    }

    startPlayingMediaSequence = async () => {
        const { choiceSequence, model, questionModel } = this.props;
        this.setState({ isMediaSequencePlaying: true });
        await playSequenceService
            .startSequence(
                getMediaModels(model, choiceSequence)
                    .map(mediaModel => mediaModel.logicalName)
            );

        interactionVisibilityService.setVisibilityTransactionDoneForQuestion(questionModel.contentRevisionId);
        this.setState({ isMediaSequencePlaying: false });
    };

    stopPlayingMediaSequence = () => {
        if (this.state.isMediaSequencePlaying) {
            clearTimeout(this.autoPlayTriggerHandle);
            playSequenceService.cancelSequence();
            this.setState({ isMediaSequencePlaying: false });
        }
    };

    isElementSelected = (choice) => {
        return this.props.userResponse.includes(choice);
    };

    handleUserResponse = (e) => {
        const { userResponse, model, callback } = this.props;
        const value = e.currentTarget.value;
        const identifier = model.identifier;

        // prevent mutating the props
        let response = Array.from(userResponse);

        if (model.cardinality === 'multiple') {
            const index = response.indexOf(value);
            if (index > -1) {
                response.splice(index, 1);
            } else {
                response.push(value);
            }
        } else {
            response = [value];
        }

        callback(identifier, response);

        this.stopPlayingMediaSequence();
        this.navigateToScorableItem();
    };

    navigateToScorableItem() {
        const { services, model, questionModel } = this.props;
        services.scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, undefined, true);
    }

    renderPlayAllButton(isMediaSequencePlaying) {
        const onClick = isMediaSequencePlaying
            ? this.stopPlayingMediaSequence
            : this.startPlayingMediaSequence;
        return (
            <button
                className={isMediaSequencePlaying ? styles.playing : ''}
                onClick={onClick}
            >
                {
                    isMediaSequencePlaying
                        ? t('audioAlternatives.autoPlaySequence.button.stop')
                        : t('audioAlternatives.autoPlaySequence.button.playAll')
                }
            </button>
        );
    }

    shouldRenderPBQuestionRubric() {
        const { questionModel } = this.props;

        return sessionReader.isPrintStylePaperBased()
            && customFieldsService.isMPCMultiAnswer(questionModel);
    }

    renderPBQuestionRubric() {
        const { questionModel, model } = this.props;

        if (this.shouldRenderPBQuestionRubric()) {
            const scorables = scorableItemNumberingService.getScorableItemsPerResponseId(model.identifier, questionModel);
            const questionNumber = scorableItemNumberingService.getFormattedItemNumber(scorables);

            return (
                <React.Fragment>
                    <h3 className="scorableItemHeadline">{t('generic.multipleQuestionsTitle')} {questionNumber}</h3>
                    <QuestionRubricBlock question={questionModel} choiceModel={model} />
                </React.Fragment>
            );
        }
        return null;
    }

    navigateToScorableItem = () => {
        const { services, model, questionModel } = this.props;
        services.scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, undefined, true);
    };

    render() {
        const { correctResponses, isReadOnly, questionModel, model, services } = this.props;
        const { isMediaSequencePlaying } = this.state;
        const orientation = model.orientation ? model.orientation : 'vertical';
        const showPlayAllButton = model.hasAudioAlternatives && !isReadOnly;
        const interactionNumber = services.scorableItemNumberingService.getInteractionNumber(questionModel.contentRevisionId, model.identifier);
        const firstOrderNumber = services.scorableItemNumberingService.getOrderNumberOfFirstScorableItem(
            questionModel.contentRevisionId,
            model.identifier
        );
        const isActive = services.scorableItemNumberingService.isActiveScorableItem(interactionNumber);
        const isFlagged = services.scorableItemNumberingService.isFlaggedScorableItem(`${questionModel.contentRevisionId}-${firstOrderNumber}`);

        const classes = [styles.choiceInteraction, 'interaction'];
        const isPaperBased = sessionReader.isPrintStylePaperBased();

        if (isPaperBased) {
            classes.push(styles.paperBased);
            if (
                customFieldsService.isItemType(questionModel, customFieldsConstants.ITEM_TRUE_FALSE_NOTGIVEN) ||
                customFieldsService.isItemType(questionModel, customFieldsConstants.ITEM_YES_NO_NOTGIVEN)
            ) {
                classes.push(styles.printBased_TFNG_YNNG);
            }
            if (questionModel.responseModels?.length === 1 || this.shouldRenderPBQuestionRubric()) {
                classes.push('single-item-in-question');
            }
        }

        const containerAttributes = {
            className: classes.join(' '),
        };
        let groupId;

        const inputType = (model.maxChoices === 1 && model.cardinality === constants.qtiCardinality.SINGLE) ? 'radio' : 'checkbox';

        if (model.prompt) {
            groupId = `interaction_group_${model.target}`;
            containerAttributes.role = inputType === 'radio' ? 'radiogroup' : 'group';
            containerAttributes['aria-labelledby'] = groupId;
        }

        const listClassName = [orientation];
        if (themeServices.getThemeInstance().isInlineChoiceImageScalingSupported(questionModel)) {
            listClassName.push(UIConstants.SCALABLE_INTERACTION_IMAGE_CLASS);
        }

        return (
            <div {...containerAttributes}>
                {this.renderPBQuestionRubric()}
                <div className={styles.promptAndButtonRow}>
                    <Prompt
                        model={model.prompt}
                        firstOrderNumber={firstOrderNumber}
                        orderNumber={interactionNumber}
                        isActive={isActive}
                        isFlagged={isFlagged}
                        questionModel={questionModel}
                        groupId={groupId}
                    />
                    {
                        showPlayAllButton
                            ? this.renderPlayAllButton(isMediaSequencePlaying)
                            : null
                    }
                </div>

                <ul className={listClassName.join(' ')}>
                    {
                        this.sortedAlternatives.map((alternative, index) => {
                            const { content = '', identifier, isAudio, isImage, mediaModels } = alternative;
                            const inputId = `${this.instanceId}-${identifier}`;
                            const checked = this.isElementSelected(identifier);
                            let isDisabled = false;

                            let correctContent;
                            if (isAudio) {
                                correctContent = domService.fixAudioLabelContent(content);
                            } else if (isImage && mediaModels?.length > 0) {
                                correctContent = content; // Content will already be wrapped in a span;
                            } else {
                                correctContent = `<span>${content}</span>`;
                            }

                            let classname = 'text';
                            if (isAudio) {
                                classname = 'audio';
                            } else if (isImage) {
                                classname = 'image';
                            }
                            if (model.cardinality === 'multiple'
                                && model.maxChoices > 0
                                && model.maxChoices === this.props.userResponse.length
                                && !checked) {
                                isDisabled = true;
                            }

                            return (
                                <li key={identifier}>
                                    {orientation === 'horizontal' && isAudio ?
                                        <React.Fragment>
                                            <label
                                                htmlFor={inputId}
                                                dangerouslySetInnerHTML={{ __html: ` ${correctContent} ` }}
                                                disabled={isReadOnly || isDisabled}
                                                className={classname}
                                            />
                                            <input
                                                type={inputType}
                                                name={this.instanceId}
                                                id={inputId}
                                                value={identifier}
                                                checked={checked}
                                                onChange={this.handleUserResponse}
                                                onFocus={this.navigateToScorableItem}
                                                disabled={isReadOnly || isDisabled}
                                            />
                                        </React.Fragment>
                                        :
                                        <React.Fragment>
                                            {
                                                isPaperBased
                                                    ? <span className="pb-numbering">
                                                        {services.utilService.toLatinLetter(index + 1)}
                                                    </span>
                                                    : <input
                                                        type={inputType}
                                                        name={this.instanceId}
                                                        id={inputId}
                                                        value={identifier}
                                                        checked={checked}
                                                        onChange={this.handleUserResponse}
                                                        onFocus={this.navigateToScorableItem}
                                                        disabled={isReadOnly || isDisabled}
                                                    />
                                            }
                                            <label
                                                htmlFor={inputId}
                                                dangerouslySetInnerHTML={{ __html: ` ${correctContent} ` }}
                                                disabled={isReadOnly || isDisabled}
                                                className={classname}
                                            />
                                        </React.Fragment>
                                    }
                                    {
                                        correctResponses !== undefined ?
                                            <InteractionOutcomeIndicator
                                                answerOption={identifier}
                                                correctResponses={correctResponses}
                                                isAttempted={checked}
                                                onReady={this.iconTrackers[identifier]}
                                                skipUnansweredIndicator
                                                translationService={services.translationService}
                                            /> : null
                                    }
                                </li>
                            );
                        })
                    }
                </ul>
            </div>
        );
    }
}

export default ChoiceInteraction;
